"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AUDIT_PAGE_SIZE = exports.AUDIT_ACTION_META = exports.AUDIT_KIND_LABELS = exports.AUDIT_KINDS = exports.AUDIT_ACTIONS = void 0;
exports.isAuditAction = isAuditAction;
exports.isAuditKind = isAuditKind;
exports.auditKindOf = auditKindOf;
exports.auditLabelOf = auditLabelOf;
/**
 * 审计日志：回答「谁在什么时候把什么改成了什么样」。
 *
 * 只记一件事的判断标准：**客户会在电话里问起它**。
 * 所以只收三类操作 —— 产生对外可见后果的（发布/下线/删除）、涉及钱的（下单/改套餐）、
 * 涉及人的（邀请/改角色/移除）。页面每敲一个字都记，表会先在三个月内涨到几百万行，
 * 然后没人再打开这个页面。
 *
 * 三条纪律：
 *
 *   1) **与业务同事务写入**。不在事务里的话，业务回滚了日志还在，
 *      或者业务成功了日志丢了 —— 前者是假证据，后者是关键时刻没证据。
 *      宁可让审计写失败把业务一起拦下：一次 insert 都失败，说明库本身有问题，
 *      那次业务本来也不该成功。
 *   2) **记「当时」不记「现在」**。actor 的邮箱与角色、目标的名称都存文本快照。
 *      靠外键去 join 现在的表，等于让今天改名的人重写昨天的历史。
 *   3) **能改的日志不叫日志**。应用角色只有 insert/select 权限（见 0018），
 *      保留期清理走运维连接。
 *
 * 刻意不记的：登录登出。audit_logs.tenant_id 是 NOT NULL 且按租户隔离，
 * 而一个账号可以属于多个租户，登录那一刻还不知道"这次算哪个租户的"。
 * 登录线索在 PM2 日志与 `sessions` 表里。
 */
/** 动作清单。加新动作只加这里，`AUDIT_ACTION_META` 少一条就是 TS 报错 */
exports.AUDIT_ACTIONS = [
    // 页面
    'page.created',
    'page.updated',
    'page.blocks_replaced',
    'page.published',
    'page.unpublished',
    'page.deleted',
    // 商品与分类
    'product.created',
    'product.updated',
    'product.published',
    'product.unpublished',
    'product.deleted',
    'category.created',
    'category.updated',
    'category.deleted',
    // 媒体
    'media.deleted',
    // 站点与域名
    'site.updated',
    'site.address_changed',
    'site.shell_replaced',
    'site.nav_saved',
    /**
     * 建站与恢复。
     *
     * 与 `tenant.provisioned` 分开：那一条是"注册那一秒系统替他建的第一个站"，
     * 这两条是"客户自己动手加的 / 恢复的"。以后查"这个客户为什么有 3 个站"，
     * 靠前者只能看到一个，靠这两条才看得到完整过程。
     */
    'site.created',
    'site.resumed',
    /** 降档时被系统自动暂停的站点。operator 是 system，不是客户点的 */
    'site.suspended',
    'domain.bound',
    'domain.unbound',
    // 成员
    'member.invited',
    'member.joined',
    'member.role_changed',
    'member.removed',
    'invitation.revoked',
    // 计费
    'billing.order_created',
    'billing.order_cancelled',
    'billing.order_paid',
    'billing.plan_downgrade_scheduled',
    'billing.plan_downgrade_cancelled',
    // 主题订阅
    'theme.subscribed',
    'theme.renewed',
    'theme.cancel_scheduled',
    'theme.cancel_revoked',
    'theme.expired',
    // 开通
    'tenant.provisioned',
];
/** 筛选用的分组。控制台上是一排 tab，不是二十几个复选项 */
exports.AUDIT_KINDS = ['content', 'catalog', 'member', 'site', 'billing'];
exports.AUDIT_KIND_LABELS = {
    content: '页面',
    catalog: '商品与分类',
    member: '成员',
    site: '站点与域名',
    billing: '账单',
};
/**
 * 动作的显示文案。
 *
 * `label` 是动词短语，表格里接在"谁"后面：「张伟 发布了页面」。
 * 为什么不把整句拼好存进库：句子要跟着界面语言变，而库里存的是事实。
 */
exports.AUDIT_ACTION_META = {
    'page.created': { kind: 'content', label: '新建了页面' },
    'page.updated': { kind: 'content', label: '修改了页面' },
    'page.blocks_replaced': { kind: 'content', label: '调整了页面区块' },
    'page.published': { kind: 'content', label: '发布了页面' },
    'page.unpublished': { kind: 'content', label: '下线了页面' },
    'page.deleted': { kind: 'content', label: '删除了页面' },
    'product.created': { kind: 'catalog', label: '新建了商品' },
    'product.updated': { kind: 'catalog', label: '修改了商品' },
    'product.published': { kind: 'catalog', label: '上架了商品' },
    'product.unpublished': { kind: 'catalog', label: '下架了商品' },
    'product.deleted': { kind: 'catalog', label: '删除了商品' },
    'category.created': { kind: 'catalog', label: '新建了分类' },
    'category.updated': { kind: 'catalog', label: '修改了分类' },
    'category.deleted': { kind: 'catalog', label: '删除了分类' },
    // 媒体归到页面分组：客户问「图怎么没了」和「这段文字怎么没了」是同一个问题
    'media.deleted': { kind: 'content', label: '删除了素材' },
    'site.updated': { kind: 'site', label: '修改了站点设置' },
    'site.address_changed': { kind: 'site', label: '更改了站点地址' },
    'site.shell_replaced': { kind: 'site', label: '更换了页头页脚' },
    'site.nav_saved': { kind: 'site', label: '修改了导航菜单' },
    'site.created': { kind: 'site', label: '新建了站点' },
    'site.resumed': { kind: 'site', label: '恢复了站点' },
    'site.suspended': { kind: 'site', label: '暂停了超出套餐的站点' },
    'domain.bound': { kind: 'site', label: '绑定了域名' },
    'domain.unbound': { kind: 'site', label: '解绑了域名' },
    'member.invited': { kind: 'member', label: '邀请了成员' },
    'member.joined': { kind: 'member', label: '加入了团队' },
    'member.role_changed': { kind: 'member', label: '修改了成员角色' },
    'member.removed': { kind: 'member', label: '移除了成员' },
    'invitation.revoked': { kind: 'member', label: '撤销了邀请' },
    'billing.order_created': { kind: 'billing', label: '创建了订单' },
    'billing.order_cancelled': { kind: 'billing', label: '关闭了订单' },
    'billing.order_paid': { kind: 'billing', label: '完成了付款' },
    'billing.plan_downgrade_scheduled': { kind: 'billing', label: '申请了降档' },
    'billing.plan_downgrade_cancelled': { kind: 'billing', label: '撤销了降档申请' },
    // 主题订阅挂在 billing 分组下：客户查"为什么被扣了 129"时会去账单里找，
    // 给它单开一个 tab 只会让他在两个地方之间来回翻
    'theme.subscribed': { kind: 'billing', label: '订阅了主题' },
    'theme.renewed': { kind: 'billing', label: '续订了主题' },
    'theme.cancel_scheduled': { kind: 'billing', label: '申请了主题退订' },
    'theme.cancel_revoked': { kind: 'billing', label: '撤销了主题退订' },
    'theme.expired': { kind: 'billing', label: '主题订阅到期' },
    'tenant.provisioned': { kind: 'site', label: '完成了开通' },
};
function isAuditAction(value) {
    return typeof value === 'string' && exports.AUDIT_ACTIONS.includes(value);
}
function isAuditKind(value) {
    return typeof value === 'string' && exports.AUDIT_KINDS.includes(value);
}
function auditKindOf(action) {
    return isAuditAction(action) ? exports.AUDIT_ACTION_META[action].kind : null;
}
function auditLabelOf(action) {
    // 历史行里可能有已下线的 action。回退成原始代码而不是空串：
    // 显示 "page.archived" 至少能让人知道发生过什么，显示空白等于丢证据
    return isAuditAction(action) ? exports.AUDIT_ACTION_META[action].label : action;
}
/** 单页条数。50 条一页是"能一眼扫完"的上限 */
exports.AUDIT_PAGE_SIZE = 50;
//# sourceMappingURL=audit.js.map